<template>
  <VRow>
    <VCol cols="12">
      <VCard title="Master Data Used Tags">
        <VRow class="mb-4">
          <VCol>
            <VCardText>Manage your used tags efficiently using the tools below.</VCardText>
          </VCol>
          <VCol cols="auto">
            <VBtn
              @click="openPopup"
              size="small"
              class="mr-4"
            >
              Add New
              <VIcon
                class="ml-1"
                icon="ri-add-large-line"
              />
            </VBtn>
          </VCol>
        </VRow>

        <VTable>
          <thead>
            <tr>
              <th>No</th>
              <th class="text-uppercase">Type</th>
              <th class="text-uppercase text-center">Tag Name</th>
              <th class="text-uppercase text-center">IDs</th>
              <th class="text-uppercase text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(tag, index) in usedTags"
              :key="tag.id"
            >
              <td>{{ index + 1 }}</td>
              <td>{{ tag.type }}</td>
              <td class="text-center">
                {{ getTagName(+tag.tagId) }}
              </td>
              <td class="text-center">{{ Array.isArray(tag.ids) ? tag.ids.join(', ') : tag.ids }}</td>
              <td class="text-center">
                <VBtn
                  color="primary"
                  icon="ri-edit-line"
                  class="mr-2"
                  size="small"
                  @click="openEditPopup(tag)"
                >
                </VBtn>
                <VBtn
                  icon="ri-delete-bin-5-line"
                  color="error"
                  size="small"
                  @click="openDeleteConfirmation(tag.id)"
                >
                </VBtn>
              </td>
            </tr>
          </tbody>
        </VTable>
      </VCard>
    </VCol>
  </VRow>

  <!-- Popup for adding new tag -->
  <v-dialog
    max-width="500"
    v-model="isPopupActive"
  >
    <v-card title="Input Master Data Used Tags">
      <v-card-text>
        <form @submit.prevent="handleSubmit">
          <VRow>
            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="type">Type</label>
                </VCol>
                <VCol
                  cols="12"
                  md="9"
                >
                  <VSelect
                    id="type"
                    v-model="formData.type"
                    :items="['package', 'restaurant']"
                    placeholder="Select Type"
                    persistent-placeholder
                    :disabled="isEditMode"
                  />
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="tagId">Tag ID</label>
                </VCol>
                <VCol
                  cols="12"
                  md="9"
                >
                  <VSelect
                    id="tagId"
                    v-model="formData.tagId"
                    :items="tagOptions"
                    item-title="name"
                    item-value="id"
                    single-line
                    placeholder="Select Tag ID"
                    persistent-placeholder
                    :disabled="isEditMode"
                  />
                </VCol>
              </VRow>
            </VCol>
            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                >
                  <label for="ids">IDs</label>
                </VCol>
                <VCol
                  cols="12"
                  md="9"
                >
                  <div
                    v-for="(id, index) in formData.ids"
                    :key="index"
                    class="d-flex align-items-center mb-2"
                  >
                    <VTextField
                      v-model="formData.ids[index]"
                      placeholder="Enter ID"
                      type="number"
                      persistent-placeholder
                      class="flex-grow-1"
                    />
                    <!-- Hide the remove button for the first element -->
                    <VBtn
                      :style="{ visibility: index !== 0 ? 'visible' : 'hidden' }"
                      icon="ri-subtract-line"
                      color="error"
                      size="small"
                      @click="removeId(index)"
                      class="ml-2"
                    >
                    </VBtn>
                  </div>
                  <VBtn
                    icon="ri-add-large-line"
                    color="primary"
                    size="small"
                    @click="addId"
                  >
                  </VBtn>
                </VCol>
              </VRow>
            </VCol>

            <VCol cols="12">
              <VRow no-gutters>
                <VCol
                  cols="12"
                  md="3"
                />
                <VCol
                  cols="12"
                  md="9"
                >
                  <VBtn
                    type="submit"
                    class="me-4"
                    >Submit</VBtn
                  >
                  <VBtn
                    @click="closePopup"
                    color="secondary"
                    variant="outlined"
                    >Cancel</VBtn
                  >
                </VCol>
              </VRow>
            </VCol>
          </VRow>
        </form>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Confirmation Popup -->
  <v-dialog
    max-width="400"
    v-model="isDeletePopupActive"
  >
    <v-card>
      <v-card-title>Confirm Deletion</v-card-title>
      <v-card-text>Are you sure you want to delete this used tag?</v-card-text>
      <v-card-actions>
        <VBtn
          color="secondary"
          variant="outlined"
          @click="closeDeletePopup"
        >
          Cancel
        </VBtn>
        <VBtn
          color="error"
          @click="confirmDelete"
        >
          Delete
        </VBtn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Alert Message -->
  <v-snackbar
    v-model="isShowSnackBar"
    multi-line
    :globalTop="true"
    :color="alertType"
    dismissible
    class="mb-4"
    @click:close="clearAlert"
  >
    {{ alertMessage }}
  </v-snackbar>
</template>
<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { usePopUp } from '@/composables/usePopUp'
import { useFetch } from '@vueuse/core'
import { onMounted, ref } from 'vue'

const { isPopupActive, openPopup, closePopup } = usePopUp()
const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()
// Props
const props = defineProps({
  environmentId: {
    type: Number,
    required: true,
  },
})
// Reactive state
const formData = ref<{ type: string; tagId: string; ids: number[]; environmentId: number }>({
  type: 'package',
  tagId: '',
  ids: [],
  environmentId: props.environmentId,
})
const isDeletePopupActive = ref(false)
const selectedTagId = ref<number | null>(null)
const usedTags = ref<UsedTag[]>([])
const tagOptions = ref<{ id: number; name: string }[]>([])
const isEditMode = ref(false) // Track whether the popup is in edit mode
const editingTagId = ref<number | null>(null) // Track the ID of the tag being edited
// Interfaces
interface UsedTag {
  id: number
  type: string
  tagId: string
  ids: string
  environmentId: number
}

// Fetch used tags
const fetchUsedTags = async () => {
  try {
    const { data } = await useFetch(`/api/used-tags?environmentId=${formData.value.environmentId}`)
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to fetch used tags')

    usedTags.value = parsedData.data || []
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while fetching used tags.', 'error')
  }
}

// Open delete confirmation popup
const openDeleteConfirmation = (id: number) => {
  selectedTagId.value = id
  isDeletePopupActive.value = true
}

// Open the popup for editing
const openEditPopup = (tag: UsedTag) => {
  isEditMode.value = true
  editingTagId.value = tag.id
  formData.value.type = tag.type
  formData.value.tagId = tag.tagId
  formData.value.ids = Array.isArray(tag.ids) ? tag.ids : [] // Ensure it's an array
  isPopupActive.value = true
}
// Close delete confirmation popup
const closeDeletePopup = () => {
  selectedTagId.value = null
  isDeletePopupActive.value = false
}

// Handle form submission (add or edit)
const handleSubmit = async () => {
  if (!formData.value.type || !formData.value.tagId || !formData.value.ids || !formData.value.ids.length) {
    showAlert('Please fill in all required fields.', 'error')
    return
  }

  try {
    if (isEditMode.value) {
      // Update existing tag
      const updatedTag = {
        ...formData.value,
        ids: formData.value.ids.join(','), // Convert array to string
      }

      const { data } = await useFetch(`/api/used-tags?id=${editingTagId.value}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTag),
      })
      const parsedData = JSON.parse(data.value as string)

      if (!parsedData.success) throw new Error(parsedData.message || 'Failed to update used tag')

      showAlert(parsedData.message || 'Used tag updated successfully', 'success')
    } else {
      // Add new tag
      const newTag = {
        ...formData.value,
        ids: formData.value.ids.join(','), // Convert array to string
      }

      const { data } = await useFetch('/api/used-tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTag),
      })
      const parsedData = JSON.parse(data.value as string)

      if (!parsedData.success) throw new Error(parsedData.message || 'Failed to add used tag')

      showAlert(parsedData.message || 'Used tag added successfully', 'success')
    }

    resetForm()
    closePopup()
    await fetchUsedTags()
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while saving the used tag.', 'error')
  }
}

// Method to add a new ID input
const addId = () => {
  formData.value.ids.push(0) // Add a default number to the array
}
// Method to remove an ID input by index
const removeId = (index: number) => {
  formData.value.ids.splice(index, 1) // Remove the ID at the specified index
}
const getTagName = (tagId: number) => {
  const tag = tagOptions.value.find(option => option.id === tagId)
  return tag ? tag.name : 'Unknown' // Return 'Unknown' if no match is found
}
// Confirm delete
const confirmDelete = async () => {
  if (selectedTagId.value === null) return

  try {
    const { data } = await useFetch(
      `/api/used-tags?id=${selectedTagId.value}&environmentId=${formData.value.environmentId}`,
      {
        method: 'DELETE',
      },
    )
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to delete used tag')

    showAlert(parsedData.message || 'Used tag deleted successfully', 'success')
    await fetchUsedTags()
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while deleting the used tag.', 'error')
  } finally {
    closeDeletePopup()
  }
}
// Fetch tag options from the API
const fetchTagOptions = async () => {
  try {
    const { data } = await useFetch('/api/tags', { method: 'GET' })
    const parsedData = JSON.parse(data.value as string)

    if (!parsedData.success) throw new Error(parsedData.message || 'Failed to fetch tag options')

    tagOptions.value = parsedData.data || []
  } catch (err: any) {
    showAlert(err?.message || 'An error occurred while fetching tag options.', 'error')
  }
}
// Utility functions
// Reset form and mode
const resetForm = () => {
  formData.value.type = 'package' // Default type
  formData.value.tagId = '' // Reset tag ID
  formData.value.ids = [] // Reset IDs array
  formData.value.environmentId = props.environmentId // Reset environmentId to the prop value
  isEditMode.value = false // Ensure it's not in edit mode
  editingTagId.value = null // Clear the editing tag ID
}

onMounted(async () => {
  await fetchTagOptions()
  fetchUsedTags()
})
</script>
